/**
 * WorkerAttendanceRequests — the worker's OWN attendance/leave requests
 * (the 6 QuTime ApprovalRequest types) + a New-request submit flow.
 *
 * 2026-06-26 (owner: "request leave 不全"): web could previously only submit
 * leave (/me/leave) + missed-punch (Time Entries). The other 4 types
 * (overtime / business_trip / off_site_work / schedule_change) had NO web
 * path. This page now submits ALL 6 via POST /v1/me/attendance-requests —
 * the SAME endpoint + per-type field schema the mobile app uses
 * (svdy-mobile/app/attendance-requests.tsx TYPE_FIELDS). Update in lockstep.
 *
 * Backed by GET /v1/me/attendance-requests (list) + POST (create) +
 * POST /{id}/withdraw (cancel a pending one).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputLabel, MenuItem,
  Paper, Select, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import WorkerListToolbar, { matchesSubmittedFilter } from '../../components/worker/WorkerListToolbar';
import { useTranslation } from 'react-i18next';
import { ApiError, meApi } from '../../api';
import DateField from '../../components/inputs/DateField';
import { activeLocale } from '../../utils/datetime';
import { attendanceDetailLine } from '../../utils/attendanceRequestDisplay';
import type { MeShiftOption } from '../../api/me';
import { PlaceSearchField } from '../../components/location';
import { translateApiError } from '../../i18n/translateApiError';
import { FieldHint } from '../../components/primitives';
import { seedStatusFromUrl } from '../../utils/seedStatusFromUrl';

interface AttendanceRequestItem {
  id: string;
  type: string;
  status: string; // pending | approved | rejected
  data: Record<string, unknown>;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  pending_approver_names?: string[];
  approval_chain?: { level: string; status: string }[] | null;
  current_step?: number;
}

interface LeaveTypeLite { id: string; name: string; status?: string; allow_half_day?: boolean }

/** The four branches of the leave form's single question. UI only — the wire contract is
 *  unchanged: `duration` still leaves as full | half_am | half_pm and `end_date` is always
 *  sent. Mobile twin: svdy-mobile/app/attendance-requests.tsx `LeaveShape`; both must move
 *  together (attendanceRequestParity.test.ts). */
type LeaveShape = 'multi' | 'full' | 'half_am' | 'half_pm';

/** The 6 attendance request types, in picker order — matches mobile. */
const REQUEST_TYPES = [
  'leave', 'overtime', 'missed_clock_in', 'business_trip', 'off_site_work', 'schedule_change',
] as const;
type RequestType = (typeof REQUEST_TYPES)[number];

type FieldKind = 'leaveType' | 'date' | 'time' | 'text' | 'textarea' | 'duration'
  | 'place'
  // 2026-08-24. `shiftFrom` is RESOLVED for the chosen date and read-only;
  // `shiftTo` picks from the org's real timetables. Both were typed strings here
  // while mobile had offered exactly this since the shifts endpoint shipped, so an
  // approver got a shift name nobody had checked against a real timetable.
  | 'shiftFrom' | 'shiftTo';
interface FieldDef { key: string; kind: FieldKind; required?: boolean }

/** Per-type field schema — keys are the JSONB payload the backend stores
 *  (kept in sync with mobile TYPE_FIELDS + the 6 admin approval types). */
const TYPE_FIELDS: Record<RequestType, FieldDef[]> = {
  leave: [
    { key: 'leave_type', kind: 'leaveType', required: true },
    { key: 'start_date', kind: 'date', required: true },
    // The branching question sits directly under the start date, BEFORE the end date, so the
    // two are read together and the end date appears only when the branch needs one
    // (2026-09-03 redesign). Backend `_resolve_leave` reads data.duration ∈
    // {half_am,half_pm} → 0.5 day; the branch writes it.
    { key: 'duration', kind: 'duration' },
    { key: 'end_date', kind: 'date', required: true },
    { key: 'reason', kind: 'textarea', required: true },
  ],
  overtime: [
    { key: 'date', kind: 'date', required: true },
    { key: 'start_time', kind: 'time', required: true },
    { key: 'end_time', kind: 'time', required: true },
    { key: 'reason', kind: 'textarea', required: true },
  ],
  missed_clock_in: [
    { key: 'date', kind: 'date', required: true },
    { key: 'time', kind: 'time', required: true },
    { key: 'reason', kind: 'textarea', required: true },
  ],
  business_trip: [
    // `place`, not `text`. The mobile twin has offered address autocomplete on this
    // exact field since 2026-08-07; this client asked the worker to type it
    // (owner, 2026-08-24). It still accepts free typing — the picker is an
    // enhancement, not a gate.
    { key: 'destination', kind: 'place', required: true },
    { key: 'start_date', kind: 'date', required: true },
    { key: 'end_date', kind: 'date', required: true },
    { key: 'reason', kind: 'textarea', required: true },
  ],
  off_site_work: [
    { key: 'location', kind: 'place', required: true },
    // A SPAN, not a single day (owner 2026-08-25): a three-day site visit was
    // unrecordable on every surface. Same `start_date` / `end_date` keys leave
    // and business trips use — a third vocabulary for one idea is what let the
    // admin and worker clients drift apart before.
    { key: 'start_date', kind: 'date', required: true },
    { key: 'end_date', kind: 'date', required: true },
    // The window worked on EACH day of the span, not one continuous interval.
    // The labels say so; `offSiteDailyWindowHint` explains it below the fields.
    { key: 'start_time', kind: 'time' },
    { key: 'end_time', kind: 'time' },
    { key: 'reason', kind: 'textarea', required: true },
  ],
  schedule_change: [
    { key: 'date', kind: 'date', required: true },
    // The IDs are what the field spec carries, because `timetables.name` has no unique
    // constraint and the backend now rosters the worker onto `to_shift_id`. The NAMES
    // travel alongside as the snapshot the approver reads (set by the controls below), so
    // renaming a shift later does not rewrite what was asked for.
    { key: 'from_shift_id', kind: 'shiftFrom' },
    { key: 'to_shift_id', kind: 'shiftTo', required: true },
    { key: 'reason', kind: 'textarea', required: true },
  ],
};

/** Per-type label overrides. The same payload key can mean different things:
 *  `start_time` is the moment overtime began, but on an off-site SPAN it is the
 *  start of the window worked on EACH day. Labelling both "Start time" would
 *  invite a worker to read a three-day request as one continuous interval —
 *  which is the shape that was deliberately NOT chosen. An explicit map rather
 *  than a key-fallback chain, so a missing translation fails visibly instead of
 *  quietly reverting to the generic wording. */
const FIELD_LABEL_OVERRIDES: Partial<Record<RequestType, Record<string, string>>> = {
  off_site_work: { start_time: 'dailyStartTime', end_time: 'dailyEndTime' },
  // The controls now key on the timetable id; the wording is unchanged, so they point at
  // the labels that already exist rather than adding `from_shift_id` / `to_shift_id`
  // strings to nine locale files for no reader-visible difference.
  schedule_change: { from_shift_id: 'from_shift', to_shift_id: 'to_shift' },
};

const STATUS_COLOR: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning', approved: 'success', rejected: 'error',
};

/** Types whose day total is counted by the SERVER from a `start_date` / `end_date`
 *  span. Listed once here rather than repeated in each `type === ...` check, so a
 *  seventh type cannot be added to one branch and forgotten in the other two.
 *
 *  `leave` SUBMITS the number (the backend's `_extract_leave_payload` honours an
 *  explicit `days`). `off_site_work` does NOT: `create_approval` recomputes and
 *  discards whatever arrives, so sending it would be theatre. Both DISPLAY the
 *  same preview from the same endpoint. */
const SPAN_COUNTED_TYPES: RequestType[] = ['leave', 'off_site_work', 'business_trip'];

/**
 * "Name (09:00–17:00)", or just the name for a flexible shift with no fixed window.
 * Mobile twin: `shiftLabel` in svdy-mobile/app/attendance-requests.tsx — one wording for
 * one thing, so an approver sees the same string whichever client submitted it.
 */
function shiftOptionLabel(option: MeShiftOption): string {
  return option.clock_in && option.clock_out
    ? `${option.name} (${option.clock_in}–${option.clock_out})`
    : option.name;
}

function fmt(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(activeLocale());
}


const WorkerAttendanceRequests: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<AttendanceRequestItem[]>([]);
  // Owner 2026-09-03: My attendance requests 也没有像 my request 一样有状态和时间过滤. The
  // sibling process list has had one since it shipped; this list had none, so a worker with
  // a year of leave history could not find the one row they were looking for. Same four
  // buckets and the same control as `WorkerRequests`, so the two tabs of the hub behave
  // identically — the whole point of merging them into one entry.
    /**
   * The status filter can be SEEDED from `?status=`, because a click on a COUNT must land on the list
   * that count describes. The Overview's pending card links here with `?status=pending`; the sidebar
   * entry links without it and keeps this page's own default.
   *
   * Seed only — the toolbar owns the value afterwards. Syncing both ways would make the URL and the
   * control two sources of truth for one piece of state (svdy-rules §10.5b), and the `?compose=1`
   * precedent in this repo consumes-and-clears for the same reason.
   */
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>(() => {
    const seeded = seedStatusFromUrl<'all' | 'pending' | 'approved' | 'rejected'>('all');
    return seeded;
  });
  // Search, for the same reason the sibling process tab has one: this list has free text (the
  // type label and the detail line). Owner 2026-09-04 flagged the pair on Approvals; the same
  // asymmetry existed here.
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Submit dialog
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<RequestType>('leave');
  const [form, setForm] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeLite[]>([]);
  /** See LeaveShape. Explicit, not derived from the dates: deriving it would flip the branch
   *  back to single-day the instant the worker picked "multi" (end still equals start) and
   *  the field they were about to fill would vanish under them. */
  const [leaveShape, setLeaveShape] = useState<LeaveShape>('full');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await meApi.attendanceRequests.list()) as AttendanceRequestItem[];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('worker:attendanceRequests.loadFailed'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Lazy-load leave types when the form opens on the leave type.
  useEffect(() => {
    if (open && type === 'leave' && leaveTypes.length === 0) {
      meApi.leaveTypes.list()
        .then((d) => setLeaveTypes(Array.isArray(d) ? (d as LeaveTypeLite[]) : []))
        .catch(() => { /* leave type select just stays empty */ });
    }
  }, [open, type, leaveTypes.length]);

  const typeLabel = (ty: string) => t(`worker:attendanceRequests.types.${ty}`, { defaultValue: ty });
  const statusLabel = (s: string) => t(`worker:attendanceRequests.status.${s}`, { defaultValue: s });
  const fieldLabel = (k: string) => {
    const override = FIELD_LABEL_OVERRIDES[type] && FIELD_LABEL_OVERRIDES[type]![k];
    return t(`worker:attendanceRequests.field.${override || k}`, { defaultValue: k.replace(/_/g, ' ') });
  };

  const stats = useMemo(() => {
    let pending = 0, approved = 0, rejected = 0;
    for (const r of rows) {
      if (r.status === 'pending') pending += 1;
      else if (r.status === 'approved') approved += 1;
      else if (r.status === 'rejected') rejected += 1;
    }
    return { pending, approved, rejected };
  }, [rows]);

  // Client-side: this endpoint returns the worker's OWN requests, which is a bounded list
  // (tens, not thousands), so a round trip per filter click would be slower and no more
  // correct. Same reasoning as WorkerRequests.
  const visibleRows = useMemo(() => {
    // GROUPED, not exact-match. The chip set gained `in_progress` and `withdrawn` on 2026-09-10, and an
    // equality test would have left both dead here: this surface's open state is `pending` and its success
    // is `approved`. A chip that can never match anything is worse than no chip — it invites the click.
    const byStatus = rows.filter((r) => matchesSubmittedFilter(r.status, statusFilter));
    const q = query.trim().toLowerCase();
    if (!q) return byStatus;
    return byStatus.filter((r) => {
      const a = r as { type?: string };
      const haystack = [
        a.type,
        // The LOCALIZED label too, so searching 请假 finds a `leave` row rather than only the
        // English enum the worker never sees.
        a.type ? t(`worker:attendanceRequests.types.${a.type}`, { defaultValue: '' }) : '',
        attendanceDetailLine(r.data, t),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, statusFilter, query, t]);

  /** Pick a branch, and write the wire fields it implies. Mobile twin: `chooseShape`. */
  const chooseShape = (next: LeaveShape) => {
    setLeaveShape(next);
    setForm((f) => {
      const out = { ...f };
      out.duration = next === 'multi' ? 'full' : next;
      if (next !== 'multi') out.end_date = f.start_date ?? '';
      return out;
    });
  };

  const openDialog = (initialType: RequestType = 'leave') => {
    setType(initialType); setForm({}); setLeaveShape('full'); setSubmitError(null); setOpen(true);
  };
  const setField = (k: string, v: string) => setForm((f) => {
    const out = { ...f, [k]: v };
    // On a single-day branch the end date is hidden, so it has to follow the start date —
    // otherwise moving the start would submit an inverted span. Mobile twin does the same.
    if (k === 'start_date' && type === 'leave' && leaveShape !== 'multi') out.end_date = v;
    return out;
  });

  // Deep-link compose: the Leave page (+ any "request X" shortcut) routes to
  // /me/attendance-requests?compose=<type> so leave submission has ONE form
  // here (owner 2026-06-28 — consolidated the old Leave-page rich dialog).
  useEffect(() => {
    const compose = searchParams.get('compose');
    if (compose && (REQUEST_TYPES as readonly string[]).includes(compose)) {
      openDialog(compose as RequestType);
      const next = new URLSearchParams(searchParams);
      next.delete('compose');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fields = useMemo(() => {
    const all = TYPE_FIELDS[type];
    // Hidden, not disabled: the invalid combination (half a day spanning three dates) stops
    // existing instead of being offered and then dimmed. Mobile twin filters identically.
    if (type === 'leave' && leaveShape !== 'multi') {
      return all.filter((f) => f.key !== 'end_date');
    }
    return all;
  }, [type, leaveShape]);
  // Leave-eligible days for the chosen span, counted by the SERVER — it knows the org's
  // rest days and holidays. Submitted as `days` / `duration_days`, which is what
  // `_notify_leave` puts in the manager's notification ("· 3 days"): mobile has always
  // sent a count and this client sent none, so the same request produced a labelled
  // notification from a phone and an unlabelled one from a desk (2026-08-24).
  // Both counts come from the SERVER in one response: `total_days` is the
  // calendar length of the span and `business_days` excludes rest days and
  // holidays. Keeping both means neither client nor this component does any date
  // arithmetic — the admin surface's calendar subtraction is exactly how the two
  // sides came to disagree.
  const [spanCount, setSpanCount] = useState<{ total: number; working: number } | null>(null);
  useEffect(() => {
    if (!SPAN_COUNTED_TYPES.includes(type) || !form.start_date || !form.end_date) {
      setSpanCount(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await meApi.businessDays.get(form.start_date, form.end_date, false);
        if (!cancelled) setSpanCount({ total: data.total_days, working: data.business_days });
      } catch {
        // Display-only degradation: no count rather than a wrong one. Submit is NOT
        // gated on it, so a network blip cannot block an otherwise complete request.
        if (!cancelled) setSpanCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [type, form.start_date, form.end_date]);

  // Leave consumes BALANCE, so its total is the working-day count, and a
  // single-day half request short-circuits to 0.5 exactly as mobile does.
  // Off-site work is not balance-consuming: its headline number is the DURATION
  // of the assignment, with the working-day count shown beside it only when the
  // two differ (see `_apply_server_day_count`).
  const derivedDays = useMemo(() => {
    if (!SPAN_COUNTED_TYPES.includes(type)) return null;
    // For LEAVE the branch is the single source of truth for "is this one day", so the
    // preview cannot disagree with what will be submitted. Comparing the dates instead
    // would read a deep-linked start date against an untouched end date as a multi-day span
    // while the branch says one day. Mobile's `isSingleDay` reads the branch for the same
    // reason.
    const singleDay = type === 'leave'
      ? leaveShape !== 'multi'
      : !!form.start_date && form.start_date === form.end_date;
    // Leave is the only type whose headline number is the WORKING-day count, because
    // it is the number deducted from the balance. Off-site work and a trip report a
    // DURATION — how long the person is away — with the working count beside it.
    if (type === 'leave') {
      if (singleDay && (form.duration ?? 'full') !== 'full') return 0.5;
      return spanCount ? spanCount.working : null;
    }
    return spanCount ? spanCount.total : null;
  }, [type, form.start_date, form.end_date, form.duration, leaveShape, spanCount]);

  /** The working-day count, shown only when it differs from the headline
   *  duration — so an ordinary Monday-to-Wednesday request stays one clean
   *  number and a request bracketing a weekend explains its own arithmetic. */
  const derivedWorkingDays = useMemo(() => {
    if (type === 'leave' || !SPAN_COUNTED_TYPES.includes(type) || !spanCount) return null;
    return spanCount.working === spanCount.total ? null : spanCount.working;
  }, [type, spanCount]);

  // `schedule_change` needs the worker's CURRENT shift for the chosen date and the list
  // of timetables they could move to. Re-fetched when the date changes, because the
  // resolved shift is a property of that date, not of the worker.
  const [shiftCurrent, setShiftCurrent] = useState<MeShiftOption | null>(null);
  const [shiftOptions, setShiftOptions] = useState<MeShiftOption[]>([]);
  const [shiftsLoaded, setShiftsLoaded] = useState(false);

  useEffect(() => {
    if (type !== 'schedule_change') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await meApi.shifts.get(form.date || undefined);
        if (cancelled) return;
        setShiftCurrent(data.current_shift ?? null);
        setShiftOptions(data.available_shifts ?? []);
        setShiftsLoaded(true);
        // The read-only display value follows the latest resolution rather than being
        // typed, which is the whole point of the parity fix.
        //
        // The `else` is load-bearing and was missing: moving the date to a day with NO
        // schedule left the previous day's id and name in the payload, so the screen
        // said "No current shift" while the submission still carried a shift — display
        // != submit (svdy-mobile-ux §9.1). The comment above already claimed the latest
        // resolution was always reflected, and it was not when the answer was "none".
        // The server overwrites both keys on submit now, but this is the only defence
        // for an app build that predates that, and it is the honest UI either way.
        if (data.current_shift) {
          setField('from_shift_id', data.current_shift.id);
          setField('from_shift', data.current_shift.name);
        } else {
          setField('from_shift_id', '');
          setField('from_shift', '');
        }
      } catch {
        if (!cancelled) setShiftsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, form.date]);

  // A single-timetable org has nothing to switch TO, so the request cannot be satisfied.
  // Mobile blocks submit for this and says why; a web worker could previously type a
  // shift name that does not exist and have it approved.
  const isSingleShiftOrg = type === 'schedule_change' && shiftsLoaded
    && shiftOptions.length <= 1;

  const canSubmit = !isSingleShiftOrg
    && fields.every((f) => !f.required || !!form[f.key]?.trim?.());

  const handleSubmit = async () => {
    if (!canSubmit) { setSubmitError(t('worker:attendanceRequests.fillError')); return; }
    setSubmitting(true); setSubmitError(null);
    try {
      const data: Record<string, unknown> = {};
      for (const f of fields) {
        const v = form[f.key];
        if (v != null && v !== '') data[f.key] = v;
      }
      // For LEAVE the payload is a function of the BRANCH, not of which fields happen to be
      // rendered. The loop above iterates the FILTERED field list, so on a single-day branch
      // `end_date` is not in it — reading the payload from the rendered fields would submit a
      // leave request with no end date, and the worker could not fix it because the field is
      // deliberately hidden. Mobile's `handleSubmit` does exactly the same.
      //
      // This replaces the old "drop `duration` when it is full or the span is multi-day"
      // cleanup: with the branch as the single source, an inconsistent pair can no longer be
      // assembled, so there is nothing to clean up afterwards. The server still receives
      // `duration` only when it means something.
      if (type === 'leave') {
        data.start_date = form.start_date ?? '';
        data.end_date = leaveShape === 'multi'
          ? (form.end_date ?? form.start_date ?? '')
          : (form.start_date ?? '');
        if (leaveShape === 'multi' || leaveShape === 'full') delete data.duration;
        else data.duration = leaveShape;
      }
      // `days` is NOT submitted. The server recounts it in `_apply_server_leave_days`
      // and discards whatever arrives — it is the number that deducts
      // `LeaveBalance.used`, and three surfaces computing it separately is how an
      // admin-recorded Friday-to-Monday leave came to cost four days of entitlement
      // while the same request from this page cost two (2026-08-25). The count above
      // is a PREVIEW; `duration` is what tells the server a single day is a half.
      await meApi.attendanceRequests.create({ type, data });
      setToast(t('worker:attendanceRequests.toastSubmitted'));
      setOpen(false);
      await fetchRows();
    } catch (e) {
      setSubmitError(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const withdraw = async (id: string) => {
    try {
      await meApi.attendanceRequests.withdraw(id);
      setToast(t('worker:attendanceRequests.toastWithdrawn'));
      await fetchRows();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    }
  };

  const renderField = (f: FieldDef) => {
    const label = fieldLabel(f.key);
    if (f.kind === 'leaveType') {
      return (
        <FormControl fullWidth size="small" required={f.required} key={f.key}>
          <InputLabel>{label}</InputLabel>
          <Select value={form[f.key] ?? ''} label={label} onChange={(e) => setField(f.key, e.target.value as string)}>
            {leaveTypes.filter((lt) => (lt.status ?? 'active') === 'active').map((lt) => (
              <MenuItem key={lt.id} value={lt.name}>{lt.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (f.kind === 'date') {
      return <DateField key={f.key} fullWidth size="small" label={label} required={f.required}
        value={form[f.key] ?? ''} onChange={(e) => setField(f.key, e.target.value)} />;
    }
    if (f.kind === 'time') {
      return <TextField key={f.key} fullWidth size="small" type="time" label={label} required={f.required}
        InputLabelProps={{ shrink: true }} value={form[f.key] ?? ''} onChange={(e) => setField(f.key, e.target.value)} />;
    }
    if (f.kind === 'shiftFrom') {
      // Read-only and never typed, drawn with the SAME chrome as every other field on
      // this form — an outlined TextField with a floating label. It used to be a caption
      // label above a filled well, which made it the only control here with a different
      // label convention; the admin dialog copied that pattern into a two-column row and
      // the owner caught it there (2026-08-26). One treatment for one concept, and the
      // form has one horizontal origin again (§15).
      //
      // The absence of a shift is the PLACEHOLDER, not the value: grey reads as "there is
      // nothing here", where black text would read as the name of a shift.
      return (
        <TextField
          key={f.key}
          fullWidth
          size="small"
          label={label}
          value={shiftCurrent ? shiftOptionLabel(shiftCurrent) : ''}
          placeholder={shiftsLoaded ? t('worker:attendanceRequests.noCurrentShift') : '…'}
          InputProps={{ readOnly: true }}
        />
      );
    }
    if (f.kind === 'shiftTo') {
      if (isSingleShiftOrg) {
        return (
          <Alert key={f.key} severity="info" sx={{ borderRadius: 1 }}>
            {t('worker:attendanceRequests.singleShiftOrg')}
          </Alert>
        );
      }
      // The CURRENT shift is excluded — "switch to the shift I am already on" is not a
      // request, and offering it invites an approval that changes nothing.
      const switchable = shiftOptions.filter(
        (option) => !shiftCurrent || option.id !== shiftCurrent.id,
      );
      return (
        <FormControl fullWidth size="small" required={f.required} key={f.key}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={form[f.key] ?? ''}
            label={label}
            onChange={(event) => {
              const id = event.target.value as string;
              setField(f.key, id);
              // The name is a SNAPSHOT, written from the same option the id came from, so
              // the two can never describe different shifts.
              const chosen = switchable.find((option) => option.id === id);
              setField('to_shift', chosen ? chosen.name : '');
            }}
          >
            {switchable.map((option) => (
              // The ID is the value: `timetables.name` is not unique, so a name cannot
              // identify the shift the backend must roster the worker onto.
              <MenuItem key={option.id} value={option.id}>{shiftOptionLabel(option)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (f.kind === 'place') {
      return (
        <PlaceSearchField
          key={f.key}
          label={label}
          required={f.required}
          value={form[f.key] ?? ''}
          onChange={(next) => setField(f.key, next)}
        />
      );
    }
    if (f.kind === 'textarea') {
      return <TextField key={f.key} fullWidth size="small" multiline rows={2} label={label} required={f.required}
        value={form[f.key] ?? ''} onChange={(e) => setField(f.key, e.target.value)} />;
    }
    if (f.kind === 'duration') {
      // …AND only if the chosen leave type permits it. `allow_half_day` is an admin policy
      // that has been on the Leave Types screen and in GET /v1/me/leave-types all along and
      // was read by no client, so a worker could book half a day of a whole-day-only type
      // and have 0.5 deducted. The server now refuses it too (attendance/service.py
      // create_approval) — this is the front gate, that is the backstop.
      const halfDayAllowed = leaveTypes.some(
        (x) => x.name === form.leave_type && x.allow_half_day === true,
      );
      return (
        <Box key={f.key}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
            {t('worker:leave.duration')}
          </Typography>
          <ToggleButtonGroup
            size="small" exclusive value={leaveShape}
            onChange={(_, v) => { if (v) chooseShape(v as LeaveShape); }}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', borderRadius: 1, px: 1.5 } }}
          >
            <ToggleButton value="multi">{t('worker:leave.durationMulti')}</ToggleButton>
            <ToggleButton value="full">{t('worker:leave.durationFull')}</ToggleButton>
            {/* Only ONE reason a branch can be unavailable now: the leave type forbids half
                days. "your span is longer than a day" is gone by construction — a longer span
                IS the `multi` branch, so the two can no longer contradict each other. */}
            <ToggleButton value="half_am" disabled={!halfDayAllowed}>{t('worker:leave.durationHalfAm')}</ToggleButton>
            <ToggleButton value="half_pm" disabled={!halfDayAllowed}>{t('worker:leave.durationHalfPm')}</ToggleButton>
          </ToggleButtonGroup>
          {/* The RULE inline, the alternative routes behind the icon. Measured across nine
              locales the combined sentence ran 63-293 chars; see FieldHint. */}
          <FieldHint
            rule={!halfDayAllowed
              ? t('worker:leave.halfDayNotAllowedForType')
              : t('worker:leave.halfDayRule')}
            detail={t('worker:leave.halfDayAlternatives')}
          />
        </Box>
      );
    }
    return <TextField key={f.key} fullWidth size="small" label={label} required={f.required}
      value={form[f.key] ?? ''} onChange={(e) => setField(f.key, e.target.value)} />;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: embedded ? 0 : '12px', width: '100%' }}>
      {!embedded && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>
            {t('worker:attendanceRequests.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('worker:attendanceRequests.subtitle')}
          </Typography>
        </Box>
      )}

      {/* ONE toolbar, in the ONE position every worker-portal list uses. This page used to put
          `+ New request` alone on its own row above a separate stats+filter row — two rows for
          what is one band, and a third answer to "where are the controls". */}
      <WorkerListToolbar
        set="submitted"
        value={statusFilter}
        onChange={(v) => setStatusFilter(v as typeof statusFilter)}
        search={query}
        onSearchChange={setQuery}
        stats={rows.length > 0 && !loading && !error ? (
          <Typography variant="body2" color="text.secondary">
            <strong>{stats.pending}</strong> {t('worker:attendanceRequests.statPending')} ·{' '}
            <strong>{stats.approved}</strong> {t('worker:attendanceRequests.statApproved')} ·{' '}
            <strong>{stats.rejected}</strong> {t('worker:attendanceRequests.statRejected')}
          </Typography>
        ) : undefined}
        action={<Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => openDialog()}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
          {t('worker:attendanceRequests.newRequest')}
        </Button>}
      />

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : error ? (
        <Paper elevation={0} sx={{ width: '100%', p: 4, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="error">{error}</Typography>
        </Paper>
      ) : visibleRows.length === 0 ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {rows.length === 0
              ? t('worker:attendanceRequests.empty')
              : t('worker:attendanceRequests.noMatch')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {rows.length === 0
              ? t('worker:attendanceRequests.emptyHint')
              : t('worker:attendanceRequests.noMatchHint')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell>{t('worker:attendanceRequests.colType')}</TableCell>
                <TableCell>{t('worker:attendanceRequests.colDetail')}</TableCell>
                <TableCell>{t('worker:attendanceRequests.colSubmitted')}</TableCell>
                <TableCell>{t('worker:attendanceRequests.colStatus')}</TableCell>
                <TableCell align="right">{t('worker:attendanceApprovals.colAction')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{typeLabel(r.type)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{attendanceDetailLine(r.data, t) || '—'}</Typography>
                    {r.status === 'pending' && (() => {
                      const chain = r.approval_chain;
                      const step = r.current_step ?? 0;
                      const lines: string[] = [];
                      if (chain && chain.length > 1) {
                        lines.push(t('worker:attendanceApprovals.stepProgress', {
                          n: step + 1, total: chain.length,
                          level: t(`worker:attendanceApprovals.level.${chain[step]?.level}`, { defaultValue: chain[step]?.level }),
                        }));
                      }
                      if (r.pending_approver_names && r.pending_approver_names.length > 0) {
                        lines.push(t('worker:attendanceRequests.pendingWith', { names: r.pending_approver_names.join(', ') }));
                      }
                      return lines.length ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>
                          {lines.join(' · ')}
                        </Typography>
                      ) : null;
                    })()}
                  </TableCell>
                  <TableCell>{fmt(r.submitted_at)}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={STATUS_COLOR[r.status] ?? 'default'}
                      label={statusLabel(r.status)} sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                  </TableCell>
                  <TableCell align="right">
                    {r.status === 'pending' ? (
                      <Button size="small" color="warning" onClick={() => withdraw(r.id)}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', borderRadius: 1, py: 0 }}>
                        {t('worker:attendanceRequests.withdraw')}
                      </Button>
                    ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* New request dialog (svdy-ui §5 — centered Dialog for mutations). */}
      <Dialog open={open} onClose={() => !submitting && setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {t('worker:attendanceRequests.newRequest')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {/* ONE alignment context for every field (svdy-ui §11.1).
              `Request type` used to sit directly in a <Stack> while the rest of
              the fields lived in a <Grid container spacing={2}>, whose negative
              margins inset its items relative to that full-width sibling — so
              the type selector was visibly wider on both sides than the fields
              under it (owner 2026-07-28). Native CSS Grid has no such margin
              trick: full-row fields span every column, date/time fields pair up
              two per row, and every left edge lines up. */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            columnGap: 2,
            rowGap: 2,
            alignItems: 'start',
          }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('worker:attendanceRequests.typeLabel')}</InputLabel>
                <Select value={type} label={t('worker:attendanceRequests.typeLabel')}
                  onChange={(e) => { setType(e.target.value as RequestType); setForm({}); setLeaveShape('full'); }}>
                  {REQUEST_TYPES.map((ty) => <MenuItem key={ty} value={ty}>{typeLabel(ty)}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            {submitError && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Alert severity="error" variant="outlined" sx={{ fontSize: '0.8rem' }} onClose={() => setSubmitError(null)}>{submitError}</Alert>
              </Box>
            )}
            {fields.map((f) => (
              // Date/time fields pair up (start+end side by side); category
              // selectors, free text and the reason textarea take the full row.
              <Box
                key={f.key}
                sx={{
                  gridColumn: {
                    xs: '1 / -1',
                    sm: f.kind === 'date' || f.kind === 'time' ? 'auto' : '1 / -1',
                  },
                }}
              >
                {renderField(f)}
              </Box>
            ))}
          </Box>
          {type === 'off_site_work' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {t('worker:attendanceRequests.offSiteDailyWindowHint')}
            </Typography>
          )}
          {SPAN_COUNTED_TYPES.includes(type) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {t('worker:attendanceRequests.daysLabel')}{' '}
              <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {derivedDays ?? '—'}
              </Typography>
              {derivedDays != null && derivedDays > 0
                ? `  ${t('worker:attendanceRequests.autoComputed')}`
                : ''}
              {derivedWorkingDays != null
                ? `  ${t('worker:attendanceRequests.workingDaysNote', { count: derivedWorkingDays })}`
                : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={submitting} sx={{ borderRadius: 1, textTransform: 'none' }}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || submitting}
            sx={{ borderRadius: 1, textTransform: 'none' }}>
            {submitting ? <CircularProgress size={18} /> : t('worker:attendanceRequests.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkerAttendanceRequests;
